import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  XMarkIcon, 
  CloudIcon, 
  ExclamationTriangleIcon, 
  CheckCircleIcon,
  ArrowPathIcon,
  ServerIcon,
  FolderIcon,
  ChevronRightIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '../services/api';

interface CloudResource {
  id: string;
  name: string;
  type: string;
  location?: string;
  region?: string;
  tags?: Record<string, string>;
  properties?: any;
  resourceGroupName?: string;
  isSynced?: boolean;
  syncInfo?: {
    resourceId: string;
    name: string;
    status: string;
    syncedAt: string;
  };
}

interface ResourceGroup {
  id: string;
  name: string;
  location?: string;
  tags?: Record<string, string>;
  resources?: CloudResource[];
  syncedCount?: number;
  totalCount?: number;
}

interface ConnectionStatus {
  isConnected: boolean;
  errorMessage?: string;
  details?: any;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  engagementId: string;
  engagementName: string;
  subscriptionId?: string;
  subscriptionName?: string;
  onResourcesSynced?: (resources: any[]) => void;
}

const ResourceSyncModal: React.FC<Props> = ({ 
  isOpen, 
  onClose, 
  engagementId, 
  engagementName,
  subscriptionId,
  subscriptionName,
  onResourcesSynced 
}) => {
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [resourceGroups, setResourceGroups] = useState<ResourceGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [groupResources, setGroupResources] = useState<Record<string, CloudResource[]>>({});
  const [loadingResources, setLoadingResources] = useState<Record<string, boolean>>({});
  const [syncResult, setSyncResult] = useState<any>(null);
  const [showSyncResult, setShowSyncResult] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOnlyUnsynced, setShowOnlyUnsynced] = useState(false);
  const [fetchingAllResources, setFetchingAllResources] = useState(false);

  const resourceGroupsUrl = subscriptionId
    ? `/engagements/${engagementId}/subscriptions/${subscriptionId}/resource-groups`
    : `/engagements/${engagementId}/resource-groups`;

  const resourcesInGroupUrl = (resourceGroupName: string) =>
    subscriptionId
      ? `/engagements/${engagementId}/subscriptions/${subscriptionId}/resource-groups/${encodeURIComponent(resourceGroupName)}/resources`
      : `/engagements/${engagementId}/resource-groups/${encodeURIComponent(resourceGroupName)}/resources`;

  const syncResourcesUrl = subscriptionId
    ? `/engagements/${engagementId}/subscriptions/${subscriptionId}/resources/sync`
    : `/engagements/${engagementId}/resources/sync`;

  const fetchResourceGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(resourceGroupsUrl);
      setResourceGroups(response.data.resourceGroups || []);
      setConnectionStatus(response.data.connectionStatus);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch resource groups';
      setError(errorMessage);
      setConnectionStatus(err.response?.data?.connectionStatus || {
        isConnected: false,
        errorMessage
      });
    } finally {
      setLoading(false);
    }
  }, [resourceGroupsUrl]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setConnectionStatus(null);
      setResourceGroups([]);
      setExpandedGroups(new Set());
      setSelectedResources([]);
      setGroupResources({});
      setLoadingResources({});
      setSyncResult(null);
      setShowSyncResult(false);
      setError(null);
      setShowOnlyUnsynced(false);
      setFetchingAllResources(false);
      fetchResourceGroups();
    }
  }, [isOpen, engagementId, subscriptionId, fetchResourceGroups]);

  const fetchResources = async (resourceGroupName: string) => {
    try {
      setLoadingResources(prev => ({ ...prev, [resourceGroupName]: true }));
      const response = await api.get(resourcesInGroupUrl(resourceGroupName));
      setGroupResources(prev => ({ 
        ...prev, 
        [resourceGroupName]: response.data.resources || [] 
      }));

      const warnings = response.data.warnings as string[] | undefined;
      if (warnings && warnings.length > 0) {
        toast.error(
          `Some ${resourceGroupName} resources could not be fetched (${warnings.length} issue${warnings.length !== 1 ? 's' : ''}). Check IAM permissions and region settings.`,
          { duration: 6000 }
        );
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch resources';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoadingResources(prev => ({ ...prev, [resourceGroupName]: false }));
    }
  };

  const toggleResourceGroup = (resourceGroupName: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(resourceGroupName)) {
      newExpanded.delete(resourceGroupName);
    } else {
      newExpanded.add(resourceGroupName);
      // Fetch resources when expanding if not already fetched
      if (!groupResources[resourceGroupName] && !loadingResources[resourceGroupName]) {
        fetchResources(resourceGroupName);
      }
    }
    setExpandedGroups(newExpanded);
  };

  const handleResourceToggle = (resourceId: string) => {
    setSelectedResources(prev =>
      prev.includes(resourceId)
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  const getSelectedResourcesForGroup = (resourceGroupName: string) => {
    const resources = groupResources[resourceGroupName] || [];
    return selectedResources.filter(id =>
      resources.some(resource => resource.id === id)
    );
  };

  const handleGroupToggle = (resourceGroupName: string, event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation(); // Prevent expanding/collapsing the group

    const resources = groupResources[resourceGroupName] || [];
    const selectableResources = resources.filter(r => !r.isSynced);
    const selectableResourceIds = selectableResources.map(r => r.id);

    const selectedInGroup = getSelectedResourcesForGroup(resourceGroupName);
    const allSelected = selectableResourceIds.length > 0 &&
                        selectableResourceIds.every(id => selectedInGroup.includes(id));

    if (allSelected) {
      // Deselect all resources in this group
      setSelectedResources(prev =>
        prev.filter(id => !selectableResourceIds.includes(id))
      );
    } else {
      // Select all selectable resources in this group
      setSelectedResources(prev => {
        const newSelection = new Set(prev);
        selectableResourceIds.forEach(id => newSelection.add(id));
        return Array.from(newSelection);
      });
    }
  };

  const isGroupFullySelected = (resourceGroupName: string): boolean => {
    const resources = groupResources[resourceGroupName] || [];
    const selectableResources = resources.filter(r => !r.isSynced);
    if (selectableResources.length === 0) return false;

    const selectedInGroup = getSelectedResourcesForGroup(resourceGroupName);
    return selectableResources.every(r => selectedInGroup.includes(r.id));
  };

  const isGroupPartiallySelected = (resourceGroupName: string): boolean => {
    const resources = groupResources[resourceGroupName] || [];
    const selectableResources = resources.filter(r => !r.isSynced);
    if (selectableResources.length === 0) return false;

    const selectedInGroup = getSelectedResourcesForGroup(resourceGroupName);
    const selectedCount = selectableResources.filter(r => selectedInGroup.includes(r.id)).length;

    return selectedCount > 0 && selectedCount < selectableResources.length;
  };

  const getGroupSyncStatus = (resourceGroupName: string): { syncedCount: number; totalCount: number; isAllSynced: boolean; hasAny: boolean } => {
    const resources = groupResources[resourceGroupName] || [];
    const syncedCount = resources.filter(r => r.isSynced).length;
    const totalCount = resources.length;
    return {
      syncedCount,
      totalCount,
      isAllSynced: totalCount > 0 && syncedCount === totalCount,
      hasAny: totalCount > 0
    };
  };

  // Get total unsynced resources count across all loaded groups
  const getTotalUnsyncedCount = (): number => {
    let count = 0;
    Object.values(groupResources).forEach(resources => {
      count += resources.filter(r => r.isSynced !== true).length;
    });
    return count;
  };

  // Get count of groups with unsynced resources
  const getGroupsWithUnsyncedCount = (): number => {
    return resourceGroups.filter(rg => {
      const resources = groupResources[rg.name] || [];
      return resources.length > 0 && resources.some(r => r.isSynced !== true);
    }).length;
  };

  // Fetch all resources for all groups (used when filter is enabled)
  const fetchAllGroupResources = async () => {
    const groupsToFetch = resourceGroups.filter(rg => !groupResources[rg.name] || groupResources[rg.name].length === 0);

    if (groupsToFetch.length === 0) return;

    setFetchingAllResources(true);

    // Set loading state for all groups being fetched
    const loadingState: Record<string, boolean> = {};
    groupsToFetch.forEach(rg => {
      loadingState[rg.name] = true;
    });
    setLoadingResources(prev => ({ ...prev, ...loadingState }));

    // Fetch resources for all groups in parallel
    try {
      const fetchPromises = groupsToFetch.map(async (rg) => {
        try {
          const response = await api.get(resourcesInGroupUrl(rg.name));
          return { groupName: rg.name, resources: response.data.resources || [] };
        } catch (err) {
          console.error(`Failed to fetch resources for ${rg.name}:`, err);
          return { groupName: rg.name, resources: [] };
        }
      });

      const results = await Promise.all(fetchPromises);

      // Update groupResources with all fetched data
      const newGroupResources: Record<string, CloudResource[]> = {};
      results.forEach(result => {
        newGroupResources[result.groupName] = result.resources;
      });

      setGroupResources(prev => ({ ...prev, ...newGroupResources }));

      // Expand all groups that have resources
      const newExpanded = new Set(expandedGroups);
      results.forEach(result => {
        if (result.resources.length > 0) {
          newExpanded.add(result.groupName);
        }
      });
      setExpandedGroups(newExpanded);
    } finally {
      // Clear loading state
      const loadingCleared: Record<string, boolean> = {};
      groupsToFetch.forEach(rg => {
        loadingCleared[rg.name] = false;
      });
      setLoadingResources(prev => ({ ...prev, ...loadingCleared }));
      setFetchingAllResources(false);
    }
  };

  // Auto-fetch all resources when filter is enabled
  useEffect(() => {
    if (showOnlyUnsynced && resourceGroups.length > 0) {
      fetchAllGroupResources();
    }
  }, [showOnlyUnsynced, resourceGroups.length]);

  // Select all unsynced resources and trigger sync
  const handleSyncAllUnsynced = async () => {
    // First, collect all unsynced resource IDs
    const unsyncedIds: string[] = [];
    Object.values(groupResources).forEach(resources => {
      resources.filter(r => r.isSynced !== true).forEach(r => unsyncedIds.push(r.id));
    });

    if (unsyncedIds.length === 0) {
      toast.error('No unsynced resources to sync');
      return;
    }

    // Set all unsynced as selected and trigger sync
    setSelectedResources(unsyncedIds);

    // Need to wait for state update then sync
    try {
      setLoading(true);
      setError(null);

      // Group selected resources by their resource groups
      const resourcesByGroup: Record<string, string[]> = {};

      Object.entries(groupResources).forEach(([groupName, resources]) => {
        const unsyncedInGroup = resources.filter(r => r.isSynced !== true).map(r => r.id);
        if (unsyncedInGroup.length > 0) {
          resourcesByGroup[groupName] = unsyncedInGroup;
        }
      });

      // Sync resources for each group
      const syncPromises = Object.entries(resourcesByGroup).map(([groupName, resourceIds]) =>
        api.post(syncResourcesUrl, {
          resourceGroupName: groupName,
          selectedResourceIds: resourceIds
        })
      );

      const responses = await Promise.all(syncPromises);

      let totalSynced = 0;
      let totalDeleted = 0;
      let totalClosedAlerts = 0;
      let totalClosedCases = 0;
      const allDeletedResources: any[] = [];

      responses.forEach(response => {
        totalSynced += response.data.syncedCount || 0;
        if (response.data.reconciliation) {
          totalDeleted += response.data.reconciliation.deletedCount || 0;
          totalClosedAlerts += response.data.reconciliation.closedAlerts || 0;
          totalClosedCases += response.data.reconciliation.closedCases || 0;
          if (response.data.reconciliation.deletedResources) {
            allDeletedResources.push(...response.data.reconciliation.deletedResources);
          }
        }
      });

      setSyncResult({
        syncedCount: totalSynced,
        deletedCount: totalDeleted,
        closedAlerts: totalClosedAlerts,
        closedCases: totalClosedCases,
        deletedResources: allDeletedResources,
        responses
      });
      setShowSyncResult(true);

      if (totalDeleted > 0) {
        toast.error(`${totalDeleted} resource${totalDeleted !== 1 ? 's' : ''} detected as deleted from cloud`, {
          duration: 5000,
        });
      }

      if (totalSynced > 0) {
        toast.success(`Successfully synced ${totalSynced} resource${totalSynced !== 1 ? 's' : ''}`);
        if (onResourcesSynced) {
          const allSyncedResources = responses.flatMap(r => r.data.resources || []);
          onResourcesSynced(allSyncedResources);
        }
      } else if (totalDeleted === 0) {
        toast.success('No new resources to sync');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to sync resources';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setLoading(true);
      setError(null);

      // Group selected resources by their resource groups
      const resourcesByGroup: Record<string, string[]> = {};

      Object.entries(groupResources).forEach(([groupName, _resources]) => {
        const selectedInGroup = getSelectedResourcesForGroup(groupName);
        if (selectedInGroup.length > 0) {
          resourcesByGroup[groupName] = selectedInGroup;
        }
      });

      if (Object.keys(resourcesByGroup).length === 0) {
        toast.error('Please select at least one resource to sync');
        return;
      }

      // Sync resources for each group
      const syncPromises = Object.entries(resourcesByGroup).map(([groupName, resourceIds]) =>
        api.post(syncResourcesUrl, {
          resourceGroupName: groupName,
          selectedResourceIds: resourceIds
        })
      );

      const responses = await Promise.all(syncPromises);

      let totalSynced = 0;
      let totalDeleted = 0;
      let totalClosedAlerts = 0;
      let totalClosedCases = 0;
      const allDeletedResources: any[] = [];

      responses.forEach(response => {
        totalSynced += response.data.syncedCount || 0;
        if (response.data.reconciliation) {
          totalDeleted += response.data.reconciliation.deletedCount || 0;
          totalClosedAlerts += response.data.reconciliation.closedAlerts || 0;
          totalClosedCases += response.data.reconciliation.closedCases || 0;
          if (response.data.reconciliation.deletedResources) {
            allDeletedResources.push(...response.data.reconciliation.deletedResources);
          }
        }
      });

      setSyncResult({
        syncedCount: totalSynced,
        deletedCount: totalDeleted,
        closedAlerts: totalClosedAlerts,
        closedCases: totalClosedCases,
        deletedResources: allDeletedResources,
        responses
      });
      setShowSyncResult(true);

      // Show appropriate toast notifications
      if (totalDeleted > 0) {
        toast.error(`${totalDeleted} resource${totalDeleted !== 1 ? 's' : ''} detected as deleted from cloud`, {
          duration: 5000,
        });
      }

      if (totalSynced > 0) {
        toast.success(`Successfully synced ${totalSynced} resource${totalSynced !== 1 ? 's' : ''}`);
        if (onResourcesSynced) {
          const allSyncedResources = responses.flatMap(r => r.data.resources || []);
          onResourcesSynced(allSyncedResources);
        }
      } else if (totalDeleted === 0) {
        toast.success('No new resources to sync');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to sync resources';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const renderConnectionError = () => (
    <div className="text-center py-8">
      <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-red-400" />
      <h3 className="mt-4 text-lg font-medium text-gray-900">Connection Failed</h3>
      <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">
        {connectionStatus?.errorMessage || 'Unable to connect to cloud provider'}
      </p>
      <div className="mt-6 flex justify-center space-x-3">
        <button
          onClick={fetchResourceGroups}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          <ArrowPathIcon className="-ml-1 mr-2 h-4 w-4" />
          Retry Connection
        </button>
        <button
          onClick={handleClose}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderResourceTree = () => (
    <div>
      <div className="mb-4">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Select Resources to Sync
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Expand resource groups and select resources to sync from <strong>{engagementName}</strong>
          {subscriptionName && (
            <>
              {' '}
              for subscription <strong>{subscriptionName}</strong>
            </>
          )}
        </p>
      </div>
      
      {connectionStatus?.isConnected && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-800">
                Successfully connected to cloud provider
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Show unsynced filter and sync all button */}
      {connectionStatus?.isConnected && resourceGroups.length > 0 && (
        <div className="mb-4 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md p-3">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyUnsynced}
              onChange={(e) => setShowOnlyUnsynced(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">
              Show unsynced resources only
            </span>
            {fetchingAllResources && (
              <span className="ml-2 inline-flex items-center text-sm text-blue-600">
                <svg className="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </span>
            )}
            {!fetchingAllResources && getTotalUnsyncedCount() > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                {getTotalUnsyncedCount()} unsynced
              </span>
            )}
          </label>
          {getTotalUnsyncedCount() > 0 && !fetchingAllResources && (
            <button
              onClick={handleSyncAllUnsynced}
              disabled={loading || fetchingAllResources}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              Sync All Unsynced
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading resource groups...</p>
        </div>
      ) : resourceGroups.length === 0 ? (
        <div className="text-center py-8">
          <CloudIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No resource groups found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No resource groups are available for this engagement.
          </p>
        </div>
      ) : showOnlyUnsynced && fetchingAllResources ? (
        <div className="text-center py-8 border border-gray-200 rounded-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <h3 className="mt-4 text-sm font-medium text-gray-900">Loading all resources...</h3>
          <p className="mt-1 text-sm text-gray-500">
            Fetching resources from all groups to find unsynced items.
          </p>
        </div>
      ) : showOnlyUnsynced && getGroupsWithUnsyncedCount() === 0 ? (
        <div className="text-center py-8 border border-gray-200 rounded-lg">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            All resources are synced
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            All resources across all groups are already synced. Uncheck the filter to see all resources.
          </p>
          <button
            onClick={() => setShowOnlyUnsynced(false)}
            className="mt-3 inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Show all resource groups
          </button>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg p-3 max-h-96 overflow-y-auto" key={`resource-tree-${showOnlyUnsynced}`}>
          <div className="space-y-1">
            {resourceGroups
              .filter((rg) => {
                // When showing only unsynced, filter out groups that are fully synced
                if (showOnlyUnsynced) {
                  const resources = groupResources[rg.name] || [];
                  // Only show groups that have been expanded AND have unsynced resources
                  // Hide groups that haven't been expanded or are fully synced
                  return resources.length > 0 && resources.some(r => r.isSynced !== true);
                }
                return true;
              })
              .map((rg) => {
              const isExpanded = expandedGroups.has(rg.name);
              const allResources = groupResources[rg.name] || [];
              // Filter resources based on showOnlyUnsynced - explicitly check for non-true isSynced
              const resources = showOnlyUnsynced
                ? allResources.filter(r => r.isSynced !== true)
                : allResources;
              const isLoadingResources = loadingResources[rg.name];
              const isFullySelected = isGroupFullySelected(rg.name);
              const isPartiallySelected = isGroupPartiallySelected(rg.name);
              const hasSelectableResources = allResources.some(r => !r.isSynced);
              const syncStatus = getGroupSyncStatus(rg.name);

              return (
                <div key={rg.id} className="w-full">
                  {/* Resource Group Node */}
                  <div
                    className={`flex items-center py-2 px-3 text-sm rounded-md transition-colors duration-150 ${
                      isFullySelected ? 'bg-blue-50 ring-1 ring-blue-200' :
                      syncStatus.isAllSynced ? 'bg-green-50' : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center flex-1">
                      <div
                        onClick={() => toggleResourceGroup(rg.name)}
                        className="w-4 h-4 mr-1 flex-shrink-0 flex items-center justify-center cursor-pointer"
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="h-3 w-3 text-gray-600" />
                        ) : (
                          <ChevronRightIcon className="h-3 w-3 text-gray-600" />
                        )}
                      </div>
                      {isExpanded && resources.length > 0 && (
                        <input
                          type="checkbox"
                          checked={isFullySelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = isPartiallySelected;
                            }
                          }}
                          onChange={(e) => handleGroupToggle(rg.name, e)}
                          disabled={!hasSelectableResources}
                          className="mr-2 h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0 disabled:opacity-50"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <div
                        onClick={() => toggleResourceGroup(rg.name)}
                        className="flex items-center flex-1 min-w-0 cursor-pointer"
                      >
                        <FolderIcon className={`h-4 w-4 mr-2 flex-shrink-0 ${
                          syncStatus.isAllSynced ? 'text-green-500' : 'text-blue-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {rg.name}
                          </div>
                          {rg.location && (
                            <div className="text-xs text-gray-500 truncate">
                              {rg.location}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Sync status badge for resource group */}
                      {syncStatus.hasAny && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ml-2 flex-shrink-0 ${
                          syncStatus.isAllSynced
                            ? 'bg-green-100 text-green-800'
                            : syncStatus.syncedCount > 0
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-600'
                        }`}>
                          {syncStatus.isAllSynced ? (
                            <>
                              <CheckCircleIcon className="w-3 h-3 mr-1" />
                              All Synced
                            </>
                          ) : (
                            `${syncStatus.syncedCount}/${syncStatus.totalCount} synced`
                          )}
                        </span>
                      )}
                      <CloudIcon className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" />
                    </div>
                  </div>

                  {/* Resources List - Tree Child Nodes */}
                  {isExpanded && (
                    <div className="ml-5 space-y-1">
                      {isLoadingResources ? (
                        <div className="flex items-center py-2 px-3 text-sm text-gray-500">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          Loading resources...
                        </div>
                      ) : resources.length === 0 ? (
                        <div className="flex items-center py-2 px-3 text-sm text-gray-500">
                          <div className="w-4 h-4 mr-1 flex-shrink-0">
                            <div className="w-2 h-2 border-l border-b border-gray-300 ml-1"></div>
                          </div>
                          <ServerIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                          {showOnlyUnsynced && allResources.length > 0
                            ? 'No unsynced resources (all synced)'
                            : 'No resources found'}
                        </div>
                      ) : (
                        resources.map((resource) => (
                          <div
                            key={resource.id}
                            className={`flex items-center py-2 px-3 text-sm rounded-md transition-colors duration-150 ${
                              resource.isSynced ? 'bg-green-50' : 'hover:bg-gray-100'
                            } ${
                              selectedResources.includes(resource.id) 
                                ? 'bg-blue-50 ring-1 ring-blue-200' 
                                : ''
                            }`}
                          >
                            <div className="flex items-center flex-1">
                              <div className="w-4 h-4 mr-1 flex-shrink-0">
                                <div className="w-2 h-2 border-l border-b border-gray-300 ml-1"></div>
                              </div>
                              <input
                                type="checkbox"
                                checked={selectedResources.includes(resource.id)}
                                onChange={() => handleResourceToggle(resource.id)}
                                disabled={resource.isSynced}
                                className="mr-2 h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0 disabled:opacity-50"
                              />
                              <CloudIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {resource.name}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {resource.type}
                                  {resource.region && ` • ${resource.region}`}
                                </div>
                              </div>
                              <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                                {resource.isSynced ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircleIcon className="w-3 h-3 mr-1" />
                                    Synced
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    Not Synced
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Action buttons */}
      {resourceGroups.length > 0 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {selectedResources.length} resource{selectedResources.length !== 1 ? 's' : ''} selected
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSync}
              disabled={selectedResources.length === 0 || loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              Sync {selectedResources.length > 0 ? `${selectedResources.length} ` : ''}Resources
            </button>
          </div>
        </div>
      )}
    </div>
  );


  const renderSyncResult = () => (
    <div className="py-8">
      {/* Header */}
      <div className="text-center">
        {syncResult?.deletedCount > 0 ? (
          <>
            <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-orange-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Sync Complete with Deletions Detected</h3>
          </>
        ) : syncResult?.syncedCount > 0 ? (
          <>
            <CheckCircleIcon className="mx-auto h-16 w-16 text-green-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Sync Complete!</h3>
          </>
        ) : (
          <>
            <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-yellow-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Sync Complete</h3>
          </>
        )}
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800">Resources Synced</p>
              <p className="mt-1 text-2xl font-semibold text-green-900">{syncResult?.syncedCount || 0}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-400" />
          </div>
        </div>

        {syncResult?.deletedCount > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-800">Resources Deleted</p>
                <p className="mt-1 text-2xl font-semibold text-red-900">{syncResult.deletedCount}</p>
              </div>
              <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
            </div>
          </div>
        )}
      </div>

      {/* Deletion Details */}
      {syncResult?.deletedCount > 0 && (
        <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
            <div className="ml-3 flex-1">
              <h4 className="text-sm font-medium text-orange-800">Deleted Resources Detected</h4>
              <p className="mt-1 text-sm text-orange-700">
                {syncResult.deletedCount} resource{syncResult.deletedCount !== 1 ? 's' : ''} detected as deleted from cloud provider.
                {(syncResult.closedAlerts > 0 || syncResult.closedCases > 0) && (
                  <span> Automatically closed {syncResult.closedAlerts} Alert{syncResult.closedAlerts !== 1 ? 's' : ''} and {syncResult.closedCases} Case{syncResult.closedCases !== 1 ? 's' : ''}.</span>
                )}
              </p>

              {/* List of deleted resources */}
              {syncResult.deletedResources && syncResult.deletedResources.length > 0 && (
                <div className="mt-3 space-y-2">
                  {syncResult.deletedResources.map((resource: any, idx: number) => (
                    <div key={idx} className="bg-white border border-orange-200 rounded-md p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{resource.name}</p>
                          <p className="text-xs text-gray-600 mt-1">
                            {resource.resourceType}
                            {resource.region && ` • ${resource.region}`}
                          </p>
                        </div>
                        <div className="ml-3 flex-shrink-0">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            Deleted
                          </span>
                        </div>
                      </div>
                      {(resource.closedAlerts > 0 || resource.closedCases > 0) && (
                        <div className="mt-2 flex items-center space-x-3 text-xs text-gray-600">
                          {resource.closedAlerts > 0 && (
                            <span className="flex items-center">
                              <CheckCircleIcon className="h-3 w-3 mr-1 text-green-600" />
                              {resource.closedAlerts} Alert{resource.closedAlerts !== 1 ? 's' : ''} closed
                            </span>
                          )}
                          {resource.closedCases > 0 && (
                            <span className="flex items-center">
                              <CheckCircleIcon className="h-3 w-3 mr-1 text-green-600" />
                              {resource.closedCases} Case{resource.closedCases !== 1 ? 's' : ''} closed
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Errors */}
      {syncResult?.errors && syncResult.errors.length > 0 && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-red-800">Errors occurred:</h4>
          <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
            {syncResult.errors.map((err: any, idx: number) => (
              <li key={idx}>
                {err.resourceName}: {err.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Close Button */}
      <div className="mt-6 text-center">
        <button
          onClick={handleClose}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <Dialog.Title as="h2" className="text-xl font-semibold leading-6 text-gray-900">
                      Sync Resources from Cloud
                    </Dialog.Title>
                    {subscriptionName && (
                      <p className="mt-1 text-sm font-medium text-indigo-700">
                        Subscription: {subscriptionName}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleClose}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="min-h-[400px]">
                  {connectionStatus && !connectionStatus.isConnected 
                    ? renderConnectionError()
                    : showSyncResult
                      ? renderSyncResult()
                      : renderResourceTree()
                  }
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ResourceSyncModal;